import { Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { StorageProvider, exportDBToJSON, downloadBlob, cn } from "@repo/core";
import { CsvImportModal } from "./components/CsvImportModal";
import { DemoBanner } from "./components/DemoBanner";
import { DemoReadOnlyToast } from "./components/DemoReadOnlyToast";
import { HelpFab, HelpModal } from "./components/HelpModal";
import { Layout } from "./components/Layout";
import {
  OnboardingTour,
  hasCompletedOnboarding,
  resetOnboardingFlag,
} from "./components/OnboardingTour";
import { TransactionModal } from "./components/TransactionModal";
import { CurrencyProvider } from "./hooks/useCurrency";
import { useT } from "./i18n/useT";
import { readStoredLocale, translations } from "./i18n/translations";
import { db, ensureDefaultCategoriesOnly, isDemoMode, seedBudgetData } from "./lib/db";
import { installDemoReadOnlyGuard, withDemoSeed } from "./lib/demoGuard";
import { applyScreenshotModeClass, isScreenshotMode } from "./lib/screenshotMode";
import { DashboardPage } from "./pages/DashboardPage";
import { MonthlyBudgetPage } from "./pages/MonthlyBudgetPage";
import { TransactionsPage } from "./pages/TransactionsPage";
import { SpendingCalendarPage } from "./pages/SpendingCalendarPage";
import { Rule503020Page } from "./pages/Rule503020Page";
import { DebtPayoffPage } from "./pages/DebtPayoffPage";
import { SinkingFundsPage } from "./pages/SinkingFundsPage";
import { NetWorthPage } from "./pages/NetWorthPage";
import { SettingsPage } from "./pages/SettingsPage";

export type ViewId =
  | "dashboard"
  | "monthly"
  | "transactions"
  | "calendar"
  | "rule503020"
  | "debt"
  | "sinking"
  | "networth"
  | "settings";

function AppShell({
  ready,
  view,
  setView,
  person,
  setPerson,
  year,
  setYear,
  fabOpen,
  setFabOpen,
  helpOpen,
  setHelpOpen,
  tourOpen,
  setTourOpen,
  tourShowChoice,
  setTourShowChoice,
  csvOpen,
  setCsvOpen,
  handleExport,
}: {
  ready: boolean;
  view: ViewId;
  setView: (v: ViewId) => void;
  person: string;
  setPerson: (p: string) => void;
  year: number;
  setYear: (y: number) => void;
  fabOpen: boolean;
  setFabOpen: (v: boolean) => void;
  helpOpen: boolean;
  setHelpOpen: (v: boolean) => void;
  tourOpen: boolean;
  setTourOpen: (v: boolean) => void;
  tourShowChoice: boolean;
  setTourShowChoice: (v: boolean) => void;
  csvOpen: boolean;
  setCsvOpen: (v: boolean) => void;
  handleExport: () => void;
}) {
  const { t } = useT();
  const hideChrome = isScreenshotMode();

  if (!ready) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="app-card p-6 text-center flex flex-col items-center gap-2">
          <p className="text-4xl">💰</p>
          <p className="text-sm font-semibold text-foreground">{t("app.loading")}</p>
        </div>
      </div>
    );
  }

  const renderPage = () => {
    switch (view) {
      case "dashboard":
        return <DashboardPage year={year} person={person} />;
      case "monthly":
        return <MonthlyBudgetPage year={year} person={person} />;
      case "transactions":
        return (
          <TransactionsPage
            year={year}
            person={person}
            onOpenCsvImport={() => setCsvOpen(true)}
          />
        );
      case "calendar":
        return <SpendingCalendarPage year={year} person={person} />;
      case "rule503020":
        return <Rule503020Page year={year} person={person} />;
      case "debt":
        return <DebtPayoffPage />;
      case "sinking":
        return <SinkingFundsPage />;
      case "networth":
        return <NetWorthPage />;
      case "settings":
        return (
          <SettingsPage
            onOpenHelp={() => setHelpOpen(true)}
            onReplayTour={() => {
              resetOnboardingFlag();
              setTourOpen(true);
            }}
          />
        );
    }
  };

  return (
    <>
      <div className="flex flex-col h-screen overflow-hidden">
        {isDemoMode && !hideChrome && <DemoBanner />}
        {isDemoMode && !hideChrome && <DemoReadOnlyToast />}
        <div className="flex-1 min-h-0 flex flex-col">
          <Layout
            activeView={view}
            onViewChange={setView}
            person={person}
            onPersonChange={setPerson}
            year={year}
            onYearChange={setYear}
            onExport={handleExport}
            reserveFabSpace={!hideChrome && view !== "transactions" && view !== "settings"}
          >
            {renderPage()}
          </Layout>
        </div>
      </div>

      {!hideChrome && view !== "transactions" && view !== "settings" && (
        <button
          type="button"
          onClick={() => setFabOpen(true)}
          aria-label={t("app.newTx")}
          title={t("app.newTx")}
          className={cn(
            "fixed z-40 no-print group",
            "bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] right-5",
            "lg:bottom-6 lg:right-6",
            "rounded-full bg-primary text-primary-foreground shadow-lg",
            "hover:scale-[1.03] active:scale-95 transition-transform",
            "flex items-center justify-center gap-2",
            "min-h-[3.25rem] min-w-[3.25rem]",
            "lg:min-w-0 lg:h-12 lg:px-5"
          )}
        >
          <Plus size={22} strokeWidth={2.5} className="shrink-0" aria-hidden />
          <span className="hidden lg:inline text-sm font-semibold whitespace-nowrap pr-0.5">
            {t("app.newTx")}
          </span>
          {/* Hover tooltip (all breakpoints) — complements native title */}
          <span
            role="tooltip"
            className={cn(
              "pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2",
              "px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap",
              "bg-foreground text-background shadow-md",
              "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
              "transition-opacity duration-150"
            )}
          >
            {t("app.newTx")}
          </span>
        </button>
      )}

      {!hideChrome && <HelpFab onClick={() => setHelpOpen(true)} />}

      <TransactionModal open={fabOpen} onClose={() => setFabOpen(false)} />
      <CsvImportModal open={csvOpen} onClose={() => setCsvOpen(false)} />
      <HelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onReplayTour={() => {
          resetOnboardingFlag();
          setTourShowChoice(false);
          setTourOpen(true);
        }}
      />
      <OnboardingTour
        open={tourOpen}
        showDataChoice={tourShowChoice}
        onClose={() => setTourOpen(false)}
      />
    </>
  );
}

export function App() {
  const [ready, setReady] = useState(false);
  const [view, setView] = useState<ViewId>("dashboard");
  const [person, setPerson] = useState("All");
  const [year, setYear] = useState(new Date().getFullYear());
  const [fabOpen, setFabOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [tourShowChoice, setTourShowChoice] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const initialLocale = readStoredLocale();

  useEffect(() => {
    applyScreenshotModeClass();
    document.documentElement.lang = initialLocale;
    const boot = async () => {
      if (isDemoMode) {
        await withDemoSeed(async () => {
          await seedBudgetData({
            force: true,
            wipeFirst: true,
            locale: initialLocale,
            fullYear: true,
          });
        });
        installDemoReadOnlyGuard(db);
      } else {
        // Production: empty start (default categories only) — never auto-load demo
        await ensureDefaultCategoriesOnly(initialLocale);
      }
      setReady(true);
      if (!isDemoMode && !hasCompletedOnboarding() && !isScreenshotMode()) {
        setTourShowChoice(true);
        setTourOpen(true);
      }
    };
    void boot();
  }, [initialLocale]);

  const handleExport = async () => {
    const json = await exportDBToJSON(db as import("dexie").default);
    downloadBlob(new Blob([json], { type: "application/json" }), `budget-backup-${year}.json`);
  };

  return (
    <StorageProvider
      db={db as import("dexie").default}
      appName="annual-budget"
      initialLocale={initialLocale}
      translations={translations}
    >
      <CurrencyProvider>
        <AppShell
          ready={ready}
          view={view}
          setView={setView}
          person={person}
          setPerson={setPerson}
          year={year}
          setYear={setYear}
          fabOpen={fabOpen}
          setFabOpen={setFabOpen}
          helpOpen={helpOpen}
          setHelpOpen={setHelpOpen}
          tourOpen={tourOpen}
          setTourOpen={setTourOpen}
          tourShowChoice={tourShowChoice}
          setTourShowChoice={setTourShowChoice}
          csvOpen={csvOpen}
          setCsvOpen={setCsvOpen}
          handleExport={() => void handleExport()}
        />
      </CurrencyProvider>
    </StorageProvider>
  );
}
